<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore, useStoreGetters } from 'dashboard/composables/store.js';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

import ComboBox from 'next/combobox/ComboBox.vue';
import Spinner from 'next/spinner/Spinner.vue';

const props = defineProps({
  config: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();

const inboxUiFlags = getters['inboxes/getUIFlags'];
const wgptIntegrationsUiFlags = getters['wgptIntegrations/getUIFlags'];
const websiteInboxes = getters['inboxes/getWebsiteInboxes'];

const initialInboxId = props.config?.inbox_id;
const selectedInboxId = ref(initialInboxId ? Number(initialInboxId) : '');

const isLoading = computed(() => {
  if (websiteInboxes.value.length) return false;
  return inboxUiFlags.value.isFetching;
});

const isUpdating = computed(
  () => wgptIntegrationsUiFlags.value.isUpdatingSalla
);

const inboxOptions = computed(() => {
  const noneOption = {
    value: '',
    label: t('WGPT_INTEGRATIONS.SALLA.EDITOR.SETTINGS.INBOX.NONE'),
  };
  const inboxList = websiteInboxes.value.map(inbox => ({
    value: inbox.id,
    label: inbox.name,
  }));
  return [noneOption, ...inboxList];
});

const fetchWebsiteInboxes = () => {
  store.dispatch('inboxes/getWebsiteInboxes');
};

const handleInboxChange = async inboxId => {
  const newInboxId = inboxId || null;
  if (newInboxId === selectedInboxId.value) return;

  try {
    await store.dispatch('wgptIntegrations/updateSallaSettings', {
      inbox_id: newInboxId,
    });
    selectedInboxId.value = inboxId || '';
    useAlert(t('WGPT_INTEGRATIONS.SALLA.EDITOR.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('WGPT_INTEGRATIONS.SALLA.EDITOR.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

onMounted(() => {
  fetchWebsiteInboxes();
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-col gap-2">
      <h3 class="text-base font-medium leading-6 text-n-slate-12">
        <span class="truncate">{{
          t('WGPT_INTEGRATIONS.SALLA.EDITOR.SETTINGS.INBOX.TITLE')
        }}</span>
      </h3>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('WGPT_INTEGRATIONS.SALLA.EDITOR.SETTINGS.INBOX.DESCRIPTION') }}
      </p>
    </div>

    <div v-if="isLoading" class="flex justify-center my-4">
      <Spinner class="text-n-brand" />
    </div>

    <div v-else class="mt-2">
      <ComboBox
        :model-value="selectedInboxId"
        :options="inboxOptions"
        :disabled="isUpdating"
        :placeholder="
          t('WGPT_INTEGRATIONS.SALLA.EDITOR.SETTINGS.INBOX.PLACEHOLDER')
        "
        @update:model-value="handleInboxChange"
      />
    </div>
  </div>
</template>
